import { Edit } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ColorPicker } from '@/components/ui/color-picker';
import { useNotification } from '@/components/notifications';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/loading-spinner';
import { Org } from '@/types';
import { apiRequest } from '@/utils/api-request';

const DEFAULT_PRIMARY_COLOR = '#1d76f3';

interface AdminBrandingPrimaryColorProps {
  org: Org;
  onUpdate: (updates: Partial<Org>) => void;
}

export const AdminBrandingPrimaryColor = ({
  org,
  onUpdate,
}: AdminBrandingPrimaryColorProps) => {
  const [color, setColor] = useState<string>(
    org?.branding?.primary_color || DEFAULT_PRIMARY_COLOR,
  );
  const notification = useNotification();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setColor(org?.branding?.primary_color || DEFAULT_PRIMARY_COLOR);
  }, [org]);

  const handleSaveColor = async () => {
    setIsLoading(true);
    try {
      const updatedBranding = {
        ...org?.branding,
        primary_color: color,
      };

      const response = await apiRequest({
        url: `/organizations/${org.orgid}`,
        method: 'PUT',
        target: 'platform',
        body: {
          name: org.name,
          branding: updatedBranding,
        },
      });

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success('Primary color updated successfully');
      onUpdate({
        branding: {
          ...org?.branding,
          primary_color: color,
        },
      });
    } catch (error) {
      console.error('Update primary color error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsEditMode(false);
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setColor(DEFAULT_PRIMARY_COLOR);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setColor(org?.branding?.primary_color || DEFAULT_PRIMARY_COLOR);
  };

  return (
    <Card>
      <CardContent className='flex flex-col gap-4'>
        <div className='flex justify-between w-full'>
          <div className='text-zinc-900 dark:text-zinc-50'>Primary color</div>
          {!isEditMode && (
            <Button
              onClick={() => setIsEditMode(true)}
              variant='ghost'
              size='icon'
            >
              <Edit />
            </Button>
          )}
        </div>
        {isEditMode ? (
          <div className='flex flex-col gap-4'>
            <ColorPicker value={color} onChange={setColor} />
            <div className='flex gap-1.5'>
              <Button onClick={handleReset} variant='outline'>
                Reset to Default
              </Button>
              <div className='flex-1' />
              <Button onClick={handleSaveColor}>
                {isLoading ? (
                  <>
                    <LoadingSpinner /> Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
              <Button
                disabled={isLoading}
                onClick={handleCancel}
                variant='ghost'
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className='flex items-center gap-2 font-semibold'>
            <div
              className='h-6 w-6 rounded border border-border'
              style={{ backgroundColor: color }}
            />
            <span>{color}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
